import { useEffect, useMemo, useRef, useState } from 'react'

// eslint-disable-next-line no-restricted-imports
import { FilterChangedEvent } from '@ag-grid-community/core'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { OrderStatus } from '__generated__/atlas-types'

import { FORMAT_MM_DD_YYYY, wipeDateHours } from 'utils/dates'

import { getDateFormat } from 'store/currentUser/selectors'

import { AgGridReact } from 'components/AgGrid'
import { GetContextMenuItemsParams, MenuItemDef } from 'components/AgGrid/types'
import { AgGridLayout } from 'components/AgGridLayout/AgGridLayout'

import { SyncedRows } from '../../ListView'
import { messages } from '../../constants/messages'
import { useJoorPayState } from '../../listView.hooks'
import EmptyPayOrdersOverlay from './TableOverlays/EmptyPayOrdersOverlay'
import { ActionsBar, OrderLoadFilter } from './components'
import {
  defaultColDef,
  filterDefs,
  frameworkComponents,
  getColumnDefs,
} from './joorPayOrders.configuration'
import {
  useModalStates,
  usePayOrdersFilters,
  usePayOrdersQueries,
  useSelectionState,
} from './joorPayOrders.hooks'
import { ActionBarIds } from './joorPayOrders.ids'
import {
  LoadValue,
  LoadValues,
  OrderStatuses,
  PaymentTypes,
  RequestStatuses,
  TableColumns,
  TableRowData,
} from './joorPayOrders.types'
import {
  ChangeStatusModal,
  SendInvoiceModal,
  SendPaymentRequestModal,
} from './modals'
import { AgGridFilters, AgGridFiltersRef } from 'features/Orders/components'

export function JoorPayOrders({
  onChangeToAllOrdersTab,
  onUpdateRows,
  rowsToSync,
}: JoorPayOrdersProps) {
  const dateFormat = useSelector(getDateFormat) || FORMAT_MM_DD_YYYY
  const { isJoorPayEnabled, paymentProvidersLoading } = useJoorPayState()

  const [requestAll, setRequestAll] = useState(false)

  const { orders, total, isDataLoading, requestError } = usePayOrdersQueries(
    Boolean(isJoorPayEnabled),
    requestAll,
  )

  const {
    checkedRows,
    singleActionRow,
    handleSetSingleActionRow,
    handleCheckboxSelectionChanged,
  } = useSelectionState()

  const {
    gridRef,
    isBeingFiltered,
    searchText,
    handleSearch,
    handleExport,
    onFiltersChange,
    handleResetFilters,
  } = usePayOrdersFilters()

  const agGridFiltersRef = useRef<AgGridFiltersRef>(null)

  const [isFromActionsMenu, setIsFromActionsMenu] = useState(false)
  const [firstDataRendered, setFirstDataRendered] = useState(false)

  const {
    isSendInvoiceModalOpen,
    isChangeStatusModalOpen,
    isSendPaymentReqModalOpen,
    handleSendInvoiceModal,
    handleChangeStatusModal,
    handleSendPaymentReqModal,
  } = useModalStates()

  useEffect(() => {
    if (!gridRef.current?.api || !rowsToSync.size) {
      return
    }
    const rowsToUpdate = Array.from(rowsToSync.values())
    gridRef.current?.api?.applyTransaction({
      update: rowsToUpdate,
    })
  }, [gridRef, rowsToSync])

  useEffect(() => {
    if (!gridRef.current?.api) {
      return
    }

    const timeout = setTimeout(() => {
      // Server timeout error is defined when we have no rows
      // and itemsError is true
      if (
        (requestError && !orders.length) ||
        (orders.length === 0 && !isDataLoading) ||
        !isJoorPayEnabled
      ) {
        gridRef.current?.api?.showNoRowsOverlay()
      }
    }, 100)

    return () => clearTimeout(timeout)
  }, [gridRef, requestError, orders.length, isDataLoading, isJoorPayEnabled])

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): MenuItemDef[] => {
    handleSetSingleActionRow(params.node.data)

    return params.node.data.type === PaymentTypes.JoorPayment
      ? [
          {
            name: messages.sendPaymentLink,
            action: () => {
              setIsFromActionsMenu(false)
              handleSendPaymentReqModal(true)
            },
          },
          {
            name: messages.changeOrderStatus,
            action: () => {
              setIsFromActionsMenu(false)
              handleChangeStatusModal(true)
            },
          },
        ]
      : []
  }

  const noRowsOverlayComponent = useMemo(() => {
    return EmptyPayOrdersOverlay
  }, [])

  const handleFiltersChange = (event: FilterChangedEvent) => {
    onFiltersChange(event)
    gridRef.current?.api?.deselectAll()
  }

  const handleClearFiltersClick = () => {
    handleResetFilters()
    agGridFiltersRef.current?.resetFilters()
  }

  const isBackGroundFetching = orders.length < total

  const isDataSettled =
    !isDataLoading &&
    orders.length > 0 &&
    firstDataRendered &&
    !isBackGroundFetching

  const disableFilters = !isDataSettled || !isJoorPayEnabled

  const isDataLoadedAndEmpty = !isDataLoading && orders.length === 0

  const handleUpdateRows = (updatedRows: TableRowData[]) => {
    if (updatedRows.length) {
      onUpdateRows(updatedRows)
      const transaction = gridRef.current?.api?.applyTransaction({
        update: updatedRows,
      })
      if (transaction && isBeingFiltered) {
        transaction.update.forEach((rowNode) => {
          if (!rowNode.displayed) {
            gridRef.current?.api?.deselectNode(rowNode)
          }
        })
      }
    }
  }

  const handleSuccessSentInvoices = (updatedIds: string[]) => {
    const rowsToUpdate = [] as TableRowData[]
    for (const id of updatedIds) {
      const rowNode = gridRef.current?.api?.getRowNode(id)
      const data = rowNode?.data

      if (data) {
        const now = wipeDateHours(new Date())
        if (!data[TableColumns.FirstRequest]) {
          // this means is first time sending the invoice or copying the link
          data[TableColumns.FirstRequest] = now
        }
        data[TableColumns.RequestStatus] = RequestStatuses.SENT
        data[TableColumns.LastNotified] = now
        rowsToUpdate.push(data)
      }
    }
    handleUpdateRows(rowsToUpdate)
  }

  const handleSuccessChangeStatus = (updated: Record<string, OrderStatus>) => {
    if (Object.keys(updated).length) {
      const rowsToUpdate = [] as TableRowData[]
      for (const id of Object.keys(updated)) {
        const rowNode = gridRef.current?.api?.getRowNode(id)
        const data = rowNode?.data

        if (data) {
          data[TableColumns.OrderStatus] = OrderStatuses[updated[id]]
          rowsToUpdate.push(data)
        }
      }
      handleUpdateRows(rowsToUpdate)
    }
  }

  const handleSendInvoiceAction = () => {
    setIsFromActionsMenu(true)
    if (checkedRows.length === 1) {
      handleSendPaymentReqModal(true)
    } else {
      handleSendInvoiceModal(true)
    }
  }

  const handleLoadFilterChange = (value: LoadValue) => {
    handleResetFilters()
    agGridFiltersRef.current?.resetFilters()
    setRequestAll(value === LoadValues.all)

    if (value === LoadValues.all) {
      setTimeout(() => {
        gridRef.current?.api?.hideOverlay()
      }, 0)
      setTimeout(() => {
        gridRef.current?.api?.showNoRowsOverlay()
      }, 5)
    }
  }

  const disableLoadRadios =
    (disableFilters && !isDataLoadedAndEmpty) || !isJoorPayEnabled

  return (
    <>
      <Container>
        <ActionsBar
          totalOrders={total}
          ordersCount={orders.length}
          searchText={searchText}
          onSearch={handleSearch}
          hasError={requestError}
          isDataEmpty={orders.length === 0}
          isFilterResultsEmpty={
            gridRef?.current?.api?.getDisplayedRowCount() === 0 &&
            isBeingFiltered
          }
          areRowsSelected={checkedRows.length > 0}
          isJoorPayEnabled={isJoorPayEnabled}
          onSendInvoiceClick={handleSendInvoiceAction}
          onChangeStatusClick={() => {
            setIsFromActionsMenu(true)
            handleChangeStatusModal(true)
          }}
          onExportClick={handleExport}
          testId={JoorPayOrdersTestIds.ActionsBar}
          filtersMenuComponent={
            <AgGridFilters
              id={ActionBarIds.FiltersMenu}
              ref={agGridFiltersRef}
              gridApi={gridRef.current?.api}
              filterDefs={filterDefs}
              disabled={disableFilters}
              menuPosition="left"
              isDataSettled={isDataSettled}
              hideSearch
            />
          }
        />
        <OrderLoadFilter
          onChange={handleLoadFilterChange}
          disabled={disableLoadRadios}
        />
        <StyledAgGridLayout className="ag-theme-balham">
          <AgGridReact
            ref={gridRef}
            frameworkComponents={frameworkComponents}
            columnDefs={getColumnDefs(dateFormat, isBackGroundFetching)}
            rowData={!requestError ? orders : []}
            suppressScrollOnNewData
            quickFilterText={searchText}
            getRowNodeId={({ id }) => id}
            defaultColDef={{
              ...defaultColDef,
              // This way we ensure when data is empty or bg fetching, we don't allow filtering
              // neither sorting (unnecessary actions then)
              ...((orders.length === 0 || isBackGroundFetching) && {
                suppressMenu: true,
                sortable: false,
              }),
            }}
            suppressRowClickSelection
            rowSelection="multiple"
            headerHeight={40}
            rowHeight={32}
            onSelectionChanged={handleCheckboxSelectionChanged}
            suppressContextMenu
            getContextMenuItems={getContextMenuItems}
            noRowsOverlayComponentFramework={noRowsOverlayComponent}
            noRowsOverlayComponentParams={{
              onAllOrdersClick: onChangeToAllOrdersTab,
              onClearFiltersClick: handleClearFiltersClick,
              onReloadClick: () => document.location.reload(),
              isJoorPayEnabled,
              isBeingFiltered,
              isDataLoading: isDataLoading || paymentProvidersLoading,
              hasRequestErrors: requestError,
              isRequestingAllOrders: requestAll,
            }}
            onFilterChanged={handleFiltersChange}
            onFirstDataRendered={(event): void => {
              setFirstDataRendered(true)
              event.columnApi.autoSizeColumns([
                TableColumns.RetailerName,
                TableColumns.PO,
                TableColumns.TrackingNumber,
                TableColumns.SalesRep,
              ])
            }}
            onRowDataUpdated={(event) => {
              if (event.api.getDisplayedRowCount() === 0) {
                setTimeout(() => {
                  gridRef.current?.api?.showNoRowsOverlay()
                }, 100)
              }
            }}
            tooltipShowDelay={0}
            suppressColumnVirtualisation
          />
        </StyledAgGridLayout>
      </Container>
      {isSendInvoiceModalOpen && (
        <SendInvoiceModal
          onClose={() => {
            handleSendInvoiceModal(false)
          }}
          selectedOrders={isFromActionsMenu ? checkedRows : singleActionRow}
          onSuccess={handleSuccessSentInvoices}
        />
      )}
      {isChangeStatusModalOpen && (
        <ChangeStatusModal
          onClose={() => {
            handleChangeStatusModal(false)
          }}
          selectedOrders={isFromActionsMenu ? checkedRows : singleActionRow}
          onSuccess={handleSuccessChangeStatus}
        />
      )}
      {isSendPaymentReqModalOpen && (
        <SendPaymentRequestModal
          selectedOrder={
            isFromActionsMenu ? checkedRows[0] : singleActionRow[0]
          }
          setIsOpen={handleSendPaymentReqModal}
          onSuccess={handleSuccessSentInvoices}
        />
      )}
    </>
  )
}

type JoorPayOrdersProps = {
  onChangeToAllOrdersTab: () => void
  rowsToSync: SyncedRows
  onUpdateRows: (updatedRows: TableRowData[]) => void
}

export const JoorPayOrdersTestIds = {
  ActionsBar: 'joor-pay-orders-actions-bar',
} as const

const Container = styled.div`
  height: 100%;
  width: 100vw;
  padding: 0 85px 0 20px;
  position: relative;

  .radio-group {
    margin-bottom: 8px;
  }
`

const StyledAgGridLayout = styled(AgGridLayout)`
  width: 100%;
  &.ag-theme-balham {
    --ag-row-hover-color: var(--color-secondary-300);
    --ag-column-hover-color: var(--color-secondary-300);
    height: 90%;
    .ag-root-wrapper {
      .ag-row {
        .ag-cell.ag-right-aligned-cell {
          font-weight: var(--font-weight-medium);
        }
      }

      .ag-header {
        border-radius: 5px 5px 0px 0px;
        // HACK: to center the header text
        .ag-header-cell.text-center {
          .ag-header-cell-label {
            justify-content: center;
          }
        }
        // HACK: to center the checkbox in the header
        .ag-header-cell.ag-focus-managed.checkbox {
          padding: 0;
          div:nth-child(2) {
            margin-left: 6px;
          }
        }
      }
      .ag-selection-checkbox {
        margin: 0;
        margin-left: 1px;
      }

      .ag-cell.action-cell {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .ag-cell.align-right {
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
    }
    // HACK: Removes the double horizontal scroll bar
    .ag-root-wrapper-body.ag-layout-normal.ag-focus-managed {
      .ag-root.ag-unselectable.ag-layout-normal {
        .ag-body-horizontal-scroll.ag-invisible-scrollbar {
          display: none;
        }
      }
    }

    .ag-cell-value {
      overflow: visible !important; // added this so tooltips doesn't appear cropped
    }
  }
`
